import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Card, Table, Badge, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import useToken from '../../hooks/useToken';
import { Link } from 'react-router-dom';


const SubscriptionOrders = ({ idSuscripcion }) => {
    const { token } = useToken();
    const [ordenes, setordenes] = useState([]);
    const [totalOrdenes, setTotalOrdenes] = useState(0);

    // Estados del infinite scroll
    const [pageNumber, setPageNumber] = useState(1);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);

    const observer = useRef();
    const scrollContainerRef = useRef();

    const lastOrderElementRef = useCallback(node => {
        if (loading) return;
        if (observer.current) observer.current.disconnect();

        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore) {
                setPageNumber(prevPageNumber => prevPageNumber + 1);
            }
        }, {
            root: scrollContainerRef.current,
            rootMargin: '0px',
            threshold: 1.0
        });

        if (node) observer.current.observe(node);
    }, [loading, hasMore]);

    const ObtenerOrdenes = async () => {
        if (!idSuscripcion) return;

        setLoading(true);

        try {
            const response = await fetch('/api/Suscripciones/ObtenerSubscriptionOrdenes', {
                method: "POST",
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    idSuscripcion: parseInt(idSuscripcion),
                    pageNumber: pageNumber,
                    pageSize: 5
                })
            });

            if (response.ok) {
                const data = await response.json();
                if (data.iResponseType === 1) {
                    const ObjDetalles = JSON.parse(data.detalles);
                    const detalles = ObjDetalles.Table1 || ObjDetalles.table1 || ObjDetalles.Table2 || ObjDetalles.table2 || [];

                    if (detalles.length > 0) {
                        if (detalles[0].GlobalCount !== undefined) {
                            setTotalOrdenes(detalles[0].GlobalCount);
                        }

                        setordenes(prevOrdenes => {
                            if (pageNumber === 1) {
                                return detalles;
                            } else {
                                // Evitar duplicados por seguridad
                                const nuevosIds = new Set(prevOrdenes.map(o => o.idOrden));
                                const unicos = detalles.filter(d => !nuevosIds.has(d.idOrden));
                                return [...prevOrdenes, ...unicos];
                            }
                        });

                        if (detalles.length < 5) {
                            setHasMore(false);
                        }
                    } else {
                        setHasMore(false);
                    }
                } else {
                    console.log("Error al obtener datos:", data.vMessage);
                }
            } else {
                console.log("Error en la respuesta del servidor");
            }
        } catch (error) {
            console.error("Error de conexión", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (idSuscripcion) {
            ObtenerOrdenes();
        }
    }, [idSuscripcion, pageNumber]);


    const formateaFecha = (fecha) => {
        if (!fecha) return '';
        const meses = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
        const date = new Date(fecha);
        const dia = date.getUTCDate();
        const mes = meses[date.getUTCMonth()];
        const anio = date.getUTCFullYear();
        return `${dia} ${mes} ${anio}`;
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value || 0);
    };



    return (
        <Card className="shadow-sm border-0 mb-4">
            <Card.Header className="bg-transparent border-bottom pt-4 pb-3 d-flex justify-content-between align-items-center">
                <h5 className="mb-0 fw-bold">
                    <FontAwesomeIcon icon="list" className="me-2 text-primary" />
                    Historial de Órdenes
                </h5>
                <span className="text-muted" style={{ fontSize: '0.85rem' }}>
                    {totalOrdenes > 0 ? `${totalOrdenes} órdenes` : `${ordenes.length} órdenes`}
                </span>
            </Card.Header>
            <Card.Body
                ref={scrollContainerRef}
                className="p-0 custom-scrollbar"
                style={{ maxHeight: '230px', overflowY: 'auto', overflowX: 'hidden' }}
            >
                <Table hover className="mb-0 align-middle">
                    <thead className="bg-100 dark__bg-1000 text-muted sticky-top" style={{ fontSize: '0.75rem', letterSpacing: '0.5px', zIndex: 1 }}>
                        <tr>
                            <th className="border-0 ps-4 text-uppercase">Orden</th>
                            <th className="border-0 text-uppercase">Fecha</th>
                            <th className="border-0 text-uppercase">Estatus</th>
                            <th className="border-0 pe-4 text-end text-uppercase">Monto</th>
                        </tr>
                    </thead>
                    <tbody>
                        {ordenes.map((ord, index) => {
                            const esElUltimo = ordenes.length === index + 1;

                            return (
                                <tr key={ord.idOrden} ref={esElUltimo ? lastOrderElementRef : null}>
                                    <td className="ps-4 py-3">
                                        <Link to={`/ordenes/detalle/${ord.idOrden}`} style={{ textDecoration: 'none' }}>
                                            <div className="fw-bold text-dark" style={{ fontSize: '0.9rem' }}>#{ord.idOrden}</div>
                                        </Link>
                                    </td>
                                    <td style={{ fontSize: '0.9rem', color: '#6c757d' }}>
                                        {formateaFecha(ord.dFechaVenta)}
                                    </td>
                                    <td>
                                        <div className="d-flex align-items-center gap-1">
                                            {ord.vEstatusActivo === 'CANCELADA' && (
                                                <Badge
                                                    bg="danger"
                                                    pill
                                                    className="px-3 py-1 text-capitalize"
                                                    style={{ fontSize: '0.75rem', fontWeight: 600 }}
                                                >
                                                    Cancelada
                                                </Badge>
                                            )}
                                            {!(ord.vEstatusActivo === 'CANCELADA' && ord.vEstadoOrden === 'NO PAGADA') && (
                                                <Badge
                                                    bg={
                                                        ord.vEstadoOrden === 'PAGADA' ? 'success' :
                                                        ord.vEstadoOrden === 'PARCIALMENTE PAGADA' ? 'info' :
                                                        ord.vEstadoOrden === 'NO PAGADA' ? 'warning' : 
                                                        'secondary'
                                                    }
                                                    pill
                                                    className="px-3 py-1 text-capitalize"
                                                    style={{ fontSize: '0.75rem', fontWeight: 600 }}
                                                >
                                                    {ord.vEstadoOrden ? ord.vEstadoOrden.toLowerCase() : 'Desconocido'}
                                                </Badge>
                                            )}
                                        </div>
                                    </td>
                                    <td className="pe-4 text-end">
                                        <span className="fw-bold text-dark" style={{ fontSize: '0.95rem' }}>{formatCurrency(ord.dTotal, ord.vMoneda)}</span>
                                        <span className="text-muted ms-1" style={{ fontSize: '0.75rem' }}>{ord.vMoneda || 'MXN'}</span>
                                    </td>
                                </tr>
                            );
                        })}
                        {ordenes.length === 0 && !loading && (
                            <tr>
                                <td colSpan="4" className="text-center py-4 text-muted">
                                    No hay historial de órdenes.
                                </td>
                            </tr>
                        )}
                        {loading && (
                            <tr>
                                <td colSpan="4" className="text-center py-3">
                                    <Spinner animation="border" size="sm" variant="primary" />
                                </td>
                            </tr>
                        )}
                    </tbody>
                </Table>
            </Card.Body>
        </Card>
    );
};

SubscriptionOrders.propTypes = {
    idSuscripcion: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

export default SubscriptionOrders;